import {
  createResidentPreQualificationService,
  fetchResidentPreQualificationService,
} from 'src/api/resident-pre-qualifications';
import { useUser } from 'src/features/users/useUser';
import { ApiError } from 'src/utils/build-api-client';
import useSWR, { useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';
import {
  ResidentPreQualificationCreateModel,
  ResidentPreQualificationModel,
} from './types';

function getPreQualificationKey(homeId: string, userId: string) {
  return ['/resident-pre-qualifications', homeId, userId];
}

async function fetchLatestPreQualification(homeId: string) {
  try {
    const preQualification = await fetchResidentPreQualificationService(homeId);

    return preQualification?.uniqueId ? preQualification : null;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'NOT_FOUND') {
      return null;
    }

    throw error;
  }
}

export function useResidentPreQualification({ homeId }: { homeId?: string }) {
  const { user, isLoadingUser } = useUser();
  const { data, isLoading, error } = useSWR(
    homeId && user?.userId ? getPreQualificationKey(homeId, user.userId) : null,
    () => fetchLatestPreQualification(homeId!),
    { shouldRetryOnError: false },
  );

  return {
    preQualification: data,
    isPreQualificationLoading: isLoading || isLoadingUser,
    preQualificationError: error,
  };
}

export function useCreateResidentPreQualification() {
  const { mutate } = useSWRConfig();
  const { user } = useUser();

  return useSWRMutation(
    '/resident-pre-qualifications',
    (_url: string, { arg }: { arg: ResidentPreQualificationCreateModel }) =>
      createResidentPreQualificationService(arg),
    {
      throwOnError: false,
      onSuccess: (created: ResidentPreQualificationModel) =>
        user?.userId &&
        mutate(
          getPreQualificationKey(created.homeUniqueId, user.userId),
          created,
          { revalidate: false },
        ),
    },
  );
}
